import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { api } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { FileText, Clock, CheckCircle, Loader, Search, ChevronLeft, ChevronRight, MoreVertical, Trash2, Edit3, Folder, LayoutGrid, ArrowLeft } from 'lucide-react';
import { getLanguageName, getLanguageFlag } from '../constants/languages';
import '../pages/DashboardPage.css';

const ITEMS_PER_PAGE = 20;

// History defaults to language folders - most people translate into a handful
// of languages, so a flat grid of every chat buries them. The flat grid stays
// one click away and the choice is remembered.
const VIEW_STORAGE_KEY = 'historyViewMode';
const UNKNOWN_LANG = '__unknown__';
const RECOGNISED_FOLDER = '__recognised__';

const folderOf = (chat) => (chat.service === 'recognition' ? RECOGNISED_FOLDER : chat.language_to || UNKNOWN_LANG);

// Shared translation-history grid (search, filters, cards, rename/delete,
// pagination). Used by both the dashboard (document translations) and the
// scans page (OCR translations). The parent owns the chat list and passes the
// already type-filtered subset plus sync callbacks.
function ChatHistory({ chats, onDeleted, onRenamed, emptyIcon: EmptyIcon = FileText, emptyTitle, emptyBody, cardIcon: CardIcon = FileText }) {
  const { t } = useTranslation('dashboard');
  const navigate = useNavigate();
  const { user } = useAuth();

  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [viewMode, setViewMode] = useState(() => (
    localStorage.getItem(VIEW_STORAGE_KEY) === 'grid' ? 'grid' : 'folders'
  ));
  const [openFolder, setOpenFolder] = useState(null);

  const [openMenuId, setOpenMenuId] = useState(null);
  const [renameId, setRenameId] = useState(null);
  const [renameName, setRenameName] = useState('');
  const menuRef = useRef(null);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, statusFilter, openFolder, viewMode]);

  useEffect(() => {
    localStorage.setItem(VIEW_STORAGE_KEY, viewMode);
  }, [viewMode]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setOpenMenuId(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const formatDate = (dateString) => {
    const utcString = dateString.endsWith('Z') ? dateString : dateString + 'Z';
    const date = new Date(utcString);
    const diff = new Date() - date;
    if (diff < 24 * 60 * 60 * 1000) {
      return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    }
    if (diff < 7 * 24 * 60 * 60 * 1000) {
      return date.toLocaleDateString([], { weekday: 'short' });
    }
    return date.toLocaleDateString([], { month: 'short', day: 'numeric' });
  };

  // When the account has a retention window, show when THIS chat's content is
  // due for auto-deletion (created_at + window), so it never vanishes silently.
  // Already-erased chats carry status auto_deleted and get no countdown.
  const deletionDate = (chat) => {
    const days = user?.auto_delete_days;
    if (!days || chat.status === 'auto_deleted' || chat.message_count === 0) return null;
    const created = new Date(chat.created_at.endsWith('Z') ? chat.created_at : chat.created_at + 'Z');
    return new Date(created.getTime() + days * 24 * 60 * 60 * 1000);
  };

  const getStatusIcon = (chat) => {
    if (chat.message_count === 0) {
      return <Clock size={16} className="status-icon" />;
    }
    switch (chat.status) {
      case 'completed':
        return <CheckCircle size={16} className="status-icon completed" />;
      case 'active':
        return <Loader size={16} className="status-icon active" />;
      default:
        return <Clock size={16} className="status-icon" />;
    }
  };

  const getEffectiveStatus = (chat) => {
    if (chat.message_count === 0) return 'empty';
    return chat.status || 'pending';
  };

  const filteredChats = useMemo(() => {
    return chats.filter((chat) => {
      const matchesSearch = searchQuery === '' ||
        chat.name.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesStatus = statusFilter === 'all' || getEffectiveStatus(chat) === statusFilter;
      return matchesSearch && matchesStatus;
    });
  }, [chats, searchQuery, statusFilter]);

  // One folder per target language, biggest first. Chats whose document isn't
  // uploaded yet have no language - they get their own "not set" folder rather
  // than being hidden; scans only recognised have no target and a folder of their own.
  const folders = useMemo(() => {
    const byLang = new Map();
    filteredChats.forEach((chat) => {
      const code = folderOf(chat);
      if (!byLang.has(code)) byLang.set(code, []);
      byLang.get(code).push(chat);
    });
    return [...byLang.entries()]
      .map(([code, items]) => ({ code, items }))
      .sort((a, b) => b.items.length - a.items.length);
  }, [filteredChats]);

  const showFolderGrid = viewMode === 'folders' && !openFolder;
  const visibleChats = useMemo(() => {
    if (viewMode !== 'folders' || !openFolder) return filteredChats;
    return filteredChats.filter((c) => folderOf(c) === openFolder);
  }, [filteredChats, viewMode, openFolder]);

  const totalPages = Math.ceil(visibleChats.length / ITEMS_PER_PAGE);
  const paginatedChats = useMemo(() => {
    const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
    return visibleChats.slice(startIndex, startIndex + ITEMS_PER_PAGE);
  }, [visibleChats, currentPage]);

  const folderLabel = (code) => {
    if (code === UNKNOWN_LANG) return t('folders.noLanguage');
    if (code === RECOGNISED_FOLDER) return t('folders.recognised');
    return getLanguageName(code);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleMenuClick = (e, chatId) => {
    e.stopPropagation();
    setOpenMenuId(openMenuId === chatId ? null : chatId);
  };

  const handleDelete = async (e, chatId) => {
    e.stopPropagation();
    setOpenMenuId(null);
    if (window.confirm(t('confirmDelete'))) {
      try {
        await api.deleteChat(chatId);
        onDeleted(chatId);
      } catch (error) {
        console.error('Failed to delete chat:', error);
        alert(t('deleteError'));
      }
    }
  };

  const handleRenameStart = (e, chat) => {
    e.stopPropagation();
    setOpenMenuId(null);
    setRenameId(chat.id);
    setRenameName(chat.name);
  };

  const handleRenameSubmit = async (e, chatId) => {
    e.stopPropagation();
    if (!renameName.trim()) return;
    try {
      await api.renameChat(chatId, renameName.trim());
      onRenamed(chatId, renameName.trim());
      setRenameId(null);
      setRenameName('');
    } catch (error) {
      console.error('Failed to rename chat:', error);
      alert(t('renameError'));
    }
  };

  const handleRenameCancel = (e) => {
    e.stopPropagation();
    setRenameId(null);
    setRenameName('');
  };

  if (chats.length === 0) {
    return (
      <div className="empty-state is-card">
        <EmptyIcon size={64} className="empty-icon" />
        <h2>{emptyTitle}</h2>
        <p>{emptyBody}</p>
      </div>
    );
  }

  return (
    <>
      <div className="filters-bar">
        <div className="search-box">
          <Search size={18} className="search-icon" />
          <input
            type="text"
            placeholder={t('search.placeholder')}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="search-input"
          />
        </div>
        <div className="filter-group">
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="filter-select"
          >
            <option value="all">{t('status.all')}</option>
            <option value="empty">{t('status.empty')}</option>
            <option value="pending">{t('status.pending')}</option>
            <option value="active">{t('status.active')}</option>
            <option value="completed">{t('status.completed')}</option>
          </select>
        </div>
        {filteredChats.length !== chats.length && (
          <span className="results-count">
            {t('search.resultsCount', { count: filteredChats.length, total: chats.length })}
          </span>
        )}
        <div className="history-view-toggle">
          <button
            type="button"
            className={`history-view-btn ${viewMode === 'folders' ? 'active' : ''}`}
            onClick={() => { setViewMode('folders'); setOpenFolder(null); }}
            title={t('folders.viewFolders')}
          >
            <Folder size={16} />
          </button>
          <button
            type="button"
            className={`history-view-btn ${viewMode === 'grid' ? 'active' : ''}`}
            onClick={() => { setViewMode('grid'); setOpenFolder(null); }}
            title={t('folders.viewGrid')}
          >
            <LayoutGrid size={16} />
          </button>
        </div>
      </div>

      {filteredChats.length === 0 ? (
        <div className="empty-state is-card">
          <Search size={64} className="empty-icon" />
          <h2>{t('empty.noMatching')}</h2>
          <p>{t('empty.adjustFilters')}</p>
          <button
            className="btn btn-secondary"
            onClick={() => { setSearchQuery(''); setStatusFilter('all'); }}
          >
            {t('empty.clearFilters')}
          </button>
        </div>
      ) : showFolderGrid ? (
        <div className="chats-grid folders-grid">
          {folders.map(({ code, items }) => (
            <button
              key={code}
              type="button"
              className="folder-card"
              onClick={() => setOpenFolder(code)}
            >
              <span className="folder-flag">
                {code === UNKNOWN_LANG || code === RECOGNISED_FOLDER ? <Folder size={22} /> : getLanguageFlag(code)}
              </span>
              <span className="folder-info">
                <span className="folder-name">{folderLabel(code)}</span>
                <span className="folder-count">{t('folders.count', { count: items.length })}</span>
              </span>
            </button>
          ))}
        </div>
      ) : (
        <>
          {viewMode === 'folders' && openFolder && (
            <div className="folder-breadcrumb">
              <button type="button" className="btn btn-ghost btn-sm" onClick={() => setOpenFolder(null)}>
                <ArrowLeft size={16} />
                {t('folders.back')}
              </button>
              <span className="folder-breadcrumb-title">
                {openFolder !== UNKNOWN_LANG && `${getLanguageFlag(openFolder)} `}
                {folderLabel(openFolder)}
              </span>
            </div>
          )}
          <div className="chats-grid">
            {paginatedChats.map((chat) => (
              <div
                key={chat.id}
                className="chat-card"
                onClick={() => renameId !== chat.id && navigate(`/chat/${chat.id}`)}
              >
                <div className="chat-card-header">
                  <CardIcon size={20} className="chat-icon" />
                  <div className="chat-card-actions">
                    {getStatusIcon(chat)}
                    <button
                      className="btn btn-ghost btn-icon chat-menu-btn"
                      onClick={(e) => handleMenuClick(e, chat.id)}
                    >
                      <MoreVertical size={16} />
                    </button>
                    {openMenuId === chat.id && (
                      <div className="chat-menu" ref={menuRef}>
                        <button
                          className="chat-menu-item"
                          onClick={(e) => handleRenameStart(e, chat)}
                        >
                          <Edit3 size={14} />
                          {t('card.rename')}
                        </button>
                        <button
                          className="chat-menu-item chat-menu-item-danger"
                          onClick={(e) => handleDelete(e, chat.id)}
                        >
                          <Trash2 size={14} />
                          {t('card.delete')}
                        </button>
                      </div>
                    )}
                  </div>
                </div>
                {renameId === chat.id ? (
                  <div className="rename-input-wrapper" onClick={(e) => e.stopPropagation()}>
                    <input
                      type="text"
                      className="rename-input"
                      value={renameName}
                      onChange={(e) => setRenameName(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') handleRenameSubmit(e, chat.id);
                        if (e.key === 'Escape') handleRenameCancel(e);
                      }}
                      autoFocus
                    />
                    <div className="rename-actions">
                      <button className="btn btn-sm btn-primary" onClick={(e) => handleRenameSubmit(e, chat.id)}>
                        {t('card.save')}
                      </button>
                      <button className="btn btn-sm btn-ghost" onClick={handleRenameCancel}>
                        {t('card.cancel')}
                      </button>
                    </div>
                  </div>
                ) : (
                  <h3 className="chat-name">{chat.name}</h3>
                )}
                <div className="chat-meta">
                  <span className="chat-date">{formatDate(chat.updated_at)}</span>
                  <span className="chat-messages">
                    {t('messages', { count: chat.message_count })}
                  </span>
                  {deletionDate(chat) && (
                    <span className="chat-expiry" title={t('retention.badgeTitle')}>
                      <Clock size={12} />
                      {t('retention.badge', {
                        date: deletionDate(chat).toLocaleDateString([], { month: 'short', day: 'numeric' }),
                      })}
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>

          {totalPages > 1 && (
            <div className="pagination">
              <button
                className="btn btn-ghost pagination-btn"
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
              >
                <ChevronLeft size={18} />
                {t('common:buttons.previous')}
              </button>
              <div className="pagination-pages">
                {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => {
                  const showPage = page === 1 ||
                    page === totalPages ||
                    Math.abs(page - currentPage) <= 1;
                  if (!showPage) {
                    if (page === 2 || page === totalPages - 1) {
                      return <span key={page} className="pagination-ellipsis">...</span>;
                    }
                    return null;
                  }
                  return (
                    <button
                      key={page}
                      className={`btn pagination-page ${page === currentPage ? 'active' : 'btn-ghost'}`}
                      onClick={() => handlePageChange(page)}
                    >
                      {page}
                    </button>
                  );
                })}
              </div>
              <button
                className="btn btn-ghost pagination-btn"
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
              >
                {t('common:buttons.next')}
                <ChevronRight size={18} />
              </button>
            </div>
          )}
        </>
      )}
    </>
  );
}

export default ChatHistory;
